import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { PiXLight, PiCloudArrowDownLight, PiArrowsOutSimpleLight, PiPaperPlaneTiltLight, PiImageLight, PiFilePdfLight, PiFileDocLight, PiFileXlsLight, PiFileTextLight, PiFileZipLight, PiFileVideoLight, PiFileAudioLight, PiPaperclipLight, PiPaperclipHorizontalLight } from 'react-icons/pi';
import apis from '../../../utils/apis';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, FieldGrid, Well } from '../../shared/brand/Field';
import { useAppDispatch } from '../../../store';
import { createTicketComment, fetchTicket, fetchTicketComments } from '../../../store/actions/HelpAction';

interface TicketDetailsContentProps {
  ticket: any;
}

const TicketDetailsContent: React.FC<TicketDetailsContentProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageCache, setImageCache] = useState<{ [key: string]: string }>({});
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const formattedSupportId = String(ticket.id || ticket.ticket_id).padStart(6, '0');

  // Fetch comments on component mount
  useEffect(() => {
    const loadComments = async () => {
      setIsLoadingComments(true);
      const result = await dispatch(fetchTicketComments(ticket.id) as any);
      if (result?.comments) {
        setComments(result.comments);
      }
      setIsLoadingComments(false);
    };
    loadComments();
  }, [ticket.id, dispatch]);

  const getStatusColor = (status: string): 'gray' | 'yellow' | 'blue' | 'green' | 'orange' | 'red' => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'blue';
      case 'in_progress':
        return 'blue';
      case 'closed':
        return 'green';
      case 'pending_user':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getPriorityColor = (priority: string): 'gray' | 'green' | 'yellow' | 'orange' | 'red' => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename: string, contentType?: string): boolean => {
    const ext = getFileExtension(filename).toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'];
    if (imageExtensions.includes(ext)) return true;
    return contentType?.startsWith('image/') || false;
  };

  const getFileIcon = (filename: string, contentType?: string): JSX.Element => {
    const ext = getFileExtension(filename).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(ext)) return <PiImageLight size={20} />;
    if (ext === 'pdf') return <PiFilePdfLight size={20} />;
    if (['doc', 'docx', 'docm', 'dot', 'dotx'].includes(ext)) return <PiFileDocLight size={20} />;
    if (['xls', 'xlsx', 'xlsm', 'csv'].includes(ext)) return <PiFileXlsLight size={20} />;
    if (['txt', 'md', 'json', 'xml', 'yaml', 'yml'].includes(ext)) return <PiFileTextLight size={20} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <PiFileZipLight size={20} />;
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return <PiFileVideoLight size={20} />;
    if (['mp3', 'wav', 'aac', 'flac', 'ogg'].includes(ext)) return <PiFileAudioLight size={20} />;
    return <PiPaperclipLight size={20} />;
  };

  const getImageDataUrl = async (attachment: any): Promise<string> => {
    if (imageCache[attachment.attachment_id]) {
      return imageCache[attachment.attachment_id];
    }

    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });

      const dataUrl = URL.createObjectURL(response.data);
      setImageCache(prev => ({ ...prev, [attachment.attachment_id]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.error('Failed to load image:', error);
      return '';
    }
  };

  useEffect(() => {
    if (ticket.attachments && ticket.attachments.length > 0) {
      ticket.attachments.forEach((attachment: any) => {
        if (isImageFile(attachment.filename, attachment.content_type) && !imageCache[attachment.attachment_id]) {
          getImageDataUrl(attachment);
        }
      });
    }
  }, [ticket.id, ticket.attachments]);

  const handleImageClick = async (attachment: any) => {
    const dataUrl = await getImageDataUrl(attachment);
    setSelectedImage(dataUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleDownload = async (attachment: any) => {
    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response = await apis({
        method: 'POST',
        url: `/support/tickets/${ticket.id}/attachments/upload`,
        data: formData,
      });

      if (response.data && response.data.success) {
        // Pull the ticket back down so the new attachment appears. A full page
        // reload would also throw away the comments already fetched here.
        await dispatch(fetchTicket(ticket.id) as any);
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handlePostComment = async () => {
    if (!commentText.trim()) return;

    setIsPostingComment(true);
    const result = await dispatch(createTicketComment(ticket.id, commentText) as any);
    
    if (result?.success) {
      setCommentText('');
      // Refresh comments
      const updatedComments = await dispatch(fetchTicketComments(ticket.id) as any);
      if (updatedComments?.comments) {
        setComments(updatedComments.comments);
      }
    }
    setIsPostingComment(false);
  };

  return (
    <div className="w-full h-[calc(100vh-110px)] flex gap-4 p-4">
      {/* LEFT COLUMN - Details & Attachments (60%) - Static, No Scroll */}
      <div data-tour-id="ticket-details-left" className="w-3/5 flex flex-col gap-6 overflow-hidden">
        {/* Details Card - Static */}
        <GlassCard padding="lg" className="flex-shrink-0">
          {/* Ticket ID and Title */}
          <div className="mb-6 pb-6 border-b border-hairline-soft">
            <h1 className="text-xs font-medium text-fg-muted uppercase tracking-wide mb-2">#{formattedSupportId}</h1>
            <h2 className="text-xl font-semibold text-fg-strong">{ticket.title}</h2>
          </div>

          {/* Status and Priority - Same Row */}
          <div data-tour-id="ticket-status-priority" className="mb-6">
            <FieldGrid columns={2}>
              <Field label={t('Status')}>
                <Badge color={getStatusColor(ticket.status)} className="capitalize text-xs">
                  {ticket.status || 'open'}
                </Badge>
              </Field>

              <Field label={t('Priority')}>
                <Badge color={getPriorityColor(ticket.priority)} className="capitalize text-xs">
                  {ticket.priority || 'medium'}
                </Badge>
              </Field>
            </FieldGrid>
          </div>

          {/* Created and Updated - Same Row with Time */}
          <div className="mb-6 pb-6 border-b border-hairline-soft">
            <FieldGrid columns={2}>
              <Field
                label={t('Created')}
                value={ticket.created_at ? format(new Date(ticket.created_at), 'MMM d, yyyy HH:mm') : undefined}
              />

              <Field
                label={t('Updated')}
                value={ticket.updated_at ? format(new Date(ticket.updated_at), 'MMM d, yyyy HH:mm') : undefined}
              />
            </FieldGrid>
          </div>

          {/* Description */}
          <div data-tour-id="ticket-description" className="mb-4">
            <Field label={t('Description')}>
              <div className={`text-sm text-fg-body leading-relaxed whitespace-pre-wrap ${
                (ticket.description?.length || 0) > 900
                  ? 'custom-scrollbar max-h-[200px] overflow-y-auto px-3'
                  : ''
              }`}>
                {ticket.description}
              </div>
            </Field>
          </div>

          {/* Resolution */}
          {ticket.resolution && (
            <Field label={t('Resolution')} value={ticket.resolution} />
          )}
        </GlassCard>

        {/* Attachments Card - Static */}
        <GlassCard
          data-tour-id="ticket-attachments"
          title={
            <>
              {t('Attachments')}
              {ticket.attachments && ticket.attachments.length > 0 && ` (${ticket.attachments.length})`}
            </>
          }
          actions={
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                variant="outline"
                size="small"
                icon={<PiPaperclipHorizontalLight size={14} />}
                isLoading={isUploadingFile}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('Upload')}
              </Button>
            </>
          }
          className="flex-1 overflow-hidden max-h-[280px]"
        >
          {!ticket.attachments || ticket.attachments.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-fg-muted mb-2">{t('No attachments yet')}</p>
              <p className="text-xs text-fg-muted">{t('Upload files to get started')}</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-hidden">
            {ticket.attachments.map((attachment: any) => {
                const isImage = isImageFile(attachment.filename, attachment.content_type);
                const fileIcon = getFileIcon(attachment.filename, attachment.content_type);
                const cachedImageUrl = imageCache[attachment.attachment_id];

                if (isImage) {
                  return (
                    <div key={attachment.attachment_id} className="relative group cursor-pointer rounded-lg overflow-hidden">
                      {cachedImageUrl ? (
                        <>
                          <img
                            src={cachedImageUrl}
                            alt={attachment.filename}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                            onClick={() => handleImageClick(attachment)}
                            title={t('Click to view full size')}
                          />
                          {/* A scrim is allowed here because it sits over an
                              image — the one place the quiet-glass standard
                              still permits a black overlay. */}
                          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <Button
                                variant="neutral"
                                size="small"
                                ariaLabel={t('View')}
                                tooltip={t('View')}
                                className="rounded-full !p-2"
                                icon={<PiArrowsOutSimpleLight size={18} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(attachment);
                                }}
                              />
                              <Button
                                variant="neutral"
                                size="small"
                                ariaLabel={t('Download')}
                                tooltip={t('Download')}
                                className="rounded-full !p-2"
                                icon={<PiCloudArrowDownLight size={18} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(attachment);
                                }}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <Skeleton variant="card" rows={2} className="h-40" />
                      )}
                    </div>
                  );
                } else {
                  return (
                    <Well
                      key={attachment.attachment_id}
                      className="flex items-center justify-between transition-colors hover:bg-surface-sunken"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 text-fg-body">{fileIcon}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-fg-strong truncate">{attachment.filename}</p>
                          <p className="text-xs text-fg-muted">{formatFileSize(attachment.size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        ariaLabel={t('Download')}
                        tooltip={t('Download')}
                        className="ml-2 flex-shrink-0"
                        icon={<PiCloudArrowDownLight size={16} />}
                        onClick={() => handleDownload(attachment)}
                      />
                    </Well>
                  );
                }
              })}
            </div>
          )}
        </GlassCard>
      </div>

      {/* RIGHT COLUMN - Comments (40%) - Scrollable */}
      <div data-tour-id="ticket-comments" className="w-2/5 flex flex-col h-full">
        <GlassCard
          title={t('Comments ({{count}})', { count: comments.length })}
          className="flex flex-col h-full overflow-hidden"
          bodyClassName="flex-1 min-h-0 flex flex-col"
        >
          {/* Comment Input */}
          <div className="mb-4 pb-4 border-b border-hairline-soft flex-shrink-0">
            <Textarea
              id="ticket-comment"
              label={t('Add a comment')}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('Add a comment...')}
              rows={3}
            />
            <Button
              onClick={handlePostComment}
              disabled={!commentText.trim()}
              isLoading={isPostingComment}
              variant="primary"
              size="small"
              className="mt-2 w-full"
              icon={<PiPaperPlaneTiltLight size={14} />}
            >
              {t('Post')}
            </Button>
          </div>

          {/* Comments List - Only This Scrolls */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {isLoadingComments ? (
              <Skeleton variant="list" rows={3} />
            ) : comments.length === 0 ? (
              <p className="text-xs text-fg-muted text-center py-4">{t('No comments yet')}</p>
            ) : (
              comments.map((comment: any, idx: number) => (
                <Well key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-fg-strong">{t('You')}</p>
                    <p className="text-xs text-fg-muted">
                      {comment.created_at && format(new Date(comment.created_at), 'MMM d, HH:mm')}
                    </p>
                  </div>
                  <p className="text-xs text-fg-body whitespace-pre-wrap">{comment.content}</p>
                </Well>
              ))
            )}
          </div>
        </GlassCard>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/20 backdrop-blur-[2px] animate-fade-in p-4">
          <div className="relative max-w-5xl max-h-[90vh] glass rounded-lg overflow-hidden shadow-sm">
            <img src={selectedImage} alt={t('Full size')} className="max-w-full max-h-[85vh] object-contain" />
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <Button
                variant="neutral"
                size="small"
                ariaLabel={t('Close')}
                tooltip={t('Close')}
                className="rounded-full !p-2"
                icon={<PiXLight size={20} />}
                onClick={handleCloseModal}
              />
              <Button
                variant="neutral"
                size="small"
                ariaLabel={t('Download')}
                tooltip={t('Download')}
                className="rounded-full !p-2"
                icon={<PiCloudArrowDownLight size={20} />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = selectedImage;
                  link.download = 'attachment';
                  document.body.appendChild(link);
                  link.click();
                  link.parentNode?.removeChild(link);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailsContent;
